
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Eye, Heart, MessageCircle, Share2, Play, Bookmark } from "lucide-react";
import { getPostTypeLabel } from "@/lib/post-type-labels";
import { ImagePreview } from "@/components/cards/ImagePreview";
import type { FBPost, IGPost } from "@/lib/types";


function fmt(n: number) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString("cs-CZ");
}

function Metric({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
  if (value === 0) return null;
  return (
    <div className="flex items-center gap-1.5 text-sm text-muted-foreground" title={label}>
      {icon}
      <span className="text-foreground font-medium">{fmt(value)}</span>
    </div>
  );
}

// === Facebook Top Post ===

function FBTopPost({ post, rank }: { post: FBPost; rank: number }) {
  const permalink = post.permalink_url || (post.post_id ? `https://facebook.com/${post.post_id}` : null);

  return (
    <div className="flex gap-3 p-3 rounded-lg bg-secondary/50 hover:bg-secondary/80 transition-colors">
      {/* Rank */}
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[var(--primary)]/20 text-[var(--primary)] flex items-center justify-center text-xs font-bold">
        {rank}
      </div>

      <ImagePreview src={post.post_picture} fallbackType={post.type} size="md" />

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-muted-foreground">{post.account_name}</span>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{getPostTypeLabel(post.type)}</Badge>
          {permalink && (
            <a href={permalink} target="_blank" rel="noopener noreferrer" className="ml-auto text-muted-foreground hover:text-[var(--primary)]">
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        <p className="text-sm truncate mb-2">{post.post_message || "—"}</p>
        <div className="flex flex-wrap gap-3">
          <Metric icon={<Eye className="h-3 w-3" />} value={post.post_impressions_unique} label="Dosah" />
          <Metric icon={<Heart className="h-3 w-3" />} value={post.post_reactions_total} label="Reakce" />
          <Metric icon={<MessageCircle className="h-3 w-3" />} value={post.post_comments_total} label="Komentáře" />
          <Metric icon={<Share2 className="h-3 w-3" />} value={post.post_activity_by_action_type_share} label="Sdílení" />
          <Metric icon={<Play className="h-3 w-3" />} value={post.post_video_views} label="Video zhlédnutí" />
        </div>
      </div>
    </div>
  );
}

// === Instagram Top Post ===

function IGTopPost({ post, rank }: { post: IGPost; rank: number }) {
  const imgUrl = post.media_thumbnail_url || post.media_url;

  return (
    <div className="flex gap-3 p-3 rounded-lg bg-secondary/50 hover:bg-secondary/80 transition-colors">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[var(--primary)]/20 text-[var(--primary)] flex items-center justify-center text-xs font-bold">
        {rank}
      </div>

      <ImagePreview src={imgUrl} fallbackType={post.media_product_type || post.media_type} size="md" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-muted-foreground">{post.account_name}</span>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{getPostTypeLabel(post.media_product_type || post.media_type)}</Badge>
          {post.media_permalink && (
            <a href={post.media_permalink} target="_blank" rel="noopener noreferrer" className="ml-auto text-muted-foreground hover:text-[var(--primary)]">
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        <p className="text-sm truncate mb-2">{post.media_caption || "—"}</p>
        <div className="flex flex-wrap gap-3">
          <Metric icon={<Eye className="h-3 w-3" />} value={post.media_reach} label="Dosah" />
          <Metric icon={<Heart className="h-3 w-3" />} value={post.media_like_count} label="Lajky" />
          <Metric icon={<MessageCircle className="h-3 w-3" />} value={post.media_comments_count} label="Komentáře" />
          <Metric icon={<Bookmark className="h-3 w-3" />} value={post.media_saved} label="Uložení" />
          <Metric icon={<Share2 className="h-3 w-3" />} value={post.media_shares} label="Sdílení" />
          <Metric icon={<Play className="h-3 w-3" />} value={post.media_reel_video_views} label="Zhlédnutí" />
        </div>
      </div>
    </div>
  );
}

// === Top Posts Card (wrapper) ===

interface TopFBPostsProps {
  posts: FBPost[];
  limit?: number;
  title?: string;
  sortBy?: "engagement" | "reach" | "reactions" | "video";
}

export function TopFBPosts({ posts, limit = 5, title = "Top Facebook příspěvky", sortBy = "engagement" }: TopFBPostsProps) {
  const actual = posts.filter((p) => p.post_id);

  const sorted = [...actual].sort((a, b) => {
    switch (sortBy) {
      case "reach": return b.post_impressions_unique - a.post_impressions_unique;
      case "reactions": return b.post_reactions_total - a.post_reactions_total;
      case "video": return b.post_video_views - a.post_video_views;
      default: return b.page_post_engagements - a.page_post_engagements;
    }
  }).slice(0, limit);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Žádné příspěvky v tomto období</p>
        ) : (
          sorted.map((post, i) => <FBTopPost key={post.post_id || i} post={post} rank={i + 1} />)
        )}
      </CardContent>
    </Card>
  );
}

interface TopIGPostsProps {
  posts: IGPost[];
  limit?: number;
  title?: string;
  sortBy?: "engagement" | "reach" | "likes" | "reels";
}

export function TopIGPosts({ posts, limit = 5, title = "Top Instagram příspěvky", sortBy = "engagement" }: TopIGPostsProps) {
  const actual = posts.filter((p) => p.media_id);

  const sorted = [...actual].sort((a, b) => {
    switch (sortBy) {
      case "reach": return b.media_reach - a.media_reach;
      case "likes": return b.media_like_count - a.media_like_count;
      case "reels": return b.media_reel_video_views - a.media_reel_video_views;
      default: return b.media_engagement - a.media_engagement;
    }
  }).slice(0, limit);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Žádné příspěvky v tomto období</p>
        ) : (
          sorted.map((post, i) => <IGTopPost key={post.media_id || i} post={post} rank={i + 1} />)
        )}
      </CardContent>
    </Card>
  );
}
